import { type PracticePreferenceFhirReconciliationType } from 'primary-schema';

import { type BaseItem, type NullablePartial, type TimingUnit } from '../common.js';
import { type AppointmentKind } from '../ehrTimeSlot.js';
import { type PatientLoginInput } from '../practice/preference.js';

export enum ConditionType {
  AGE = 'age',
  PAYMENT = 'payment',
  QUESTION = 'question',
  SCORE = 'score',
}

export interface BaseCondition {
  sourcePrevisitFormTemplateFieldConfigId: string | null;
  conditionalPrevisitFormTemplateFieldConfigId: string;
  acceptValues: string[] | null;
  rejectValues: string[] | null;
  requireMultiAcceptValues: boolean;
  ageMin: number | null;
  ageMax: number | null;
  property: string | null;
  min: number | null;
  max: number | null;
  type: ConditionType;
}

export interface Condition extends BaseCondition, BaseItem {}

export type UpsertCondition = NullablePartial<
  Omit<
    BaseCondition,
    'sourcePrevisitFormTemplateFieldConfigId' | 'conditionalPrevisitFormTemplateFieldConfigId'
  > & { sourcePrevisitFormTemplateFieldConfigName?: string }
>;

export enum Primitive {
  Address = 'ADDRESS',
  AppointmentConfirmation = 'APPOINTMENT_CONFIRMATION',
  BodyDiagram = 'BODY_DIAGRAM',
  Boolean = 'BOOLEAN',
  ButtonLink = 'BUTTON_LINK',
  CustomContent = 'CUSTOM_CONTENT',
  Date = 'DATE',
  DocumentSigner = 'DOCUMENT_SIGNER',
  Email = 'EMAIL',
  Enum = 'ENUM',
  FreehandBodyDiagram = 'FREEHAND_BODY_DIAGRAM',
  ImageUploader = 'IMAGE_UPLOADER',
  MultiEnum = 'MULTI_ENUM',
  MultiScheduleAppointment = 'MULTI_SCHEDULE_APPOINTMENT',
  MultiString = 'MULTI_STRING',
  Number = 'NUMBER',
  PatientAddressMailing = 'PATIENT_ADDRESS_MAILING',
  PatientAddressMailingSameAsPhysical = 'PATIENT_ADDRESS_MAILING_SAME_AS_PHYSICAL',
  PatientAddressPhysical = 'PATIENT_ADDRESS_PHYSICAL',
  PatientAlcoholUsage = 'PATIENT_ALCOHOL_USAGE',
  PatientAlcoholUsageConcern = 'PATIENT_ALCOHOL_USAGE_CONCERN',
  PatientAllergies = 'PATIENT_ALLERGIES',
  PatientBilling = 'PATIENT_BILLING',
  PatientBillingDetails = 'PATIENT_BILLING_DETAILS',
  PatientCareProviders = 'PATIENT_CARE_PROVIDERS',
  PatientCareProvidersPicker = 'PATIENT_CARE_PROVIDERS_PICKER',
  PatientCellPhone = 'PATIENT_CELL_PHONE',
  PatientDob = 'PATIENT_DOB',
  PatientEmail = 'PATIENT_EMAIL',
  PatientEmergencyContact = 'PATIENT_EMERGENCY_CONTACT',
  PatientEmergencyContacts = 'PATIENT_EMERGENCY_CONTACTS',
  PatientEmployer = 'PATIENT_EMPLOYER',
  PatientEmploymentStatus = 'PATIENT_EMPLOYMENT_STATUS',
  PatientEnvironment = 'PATIENT_ENVIRONMENT',
  PatientEnvironmentPediatric = 'PATIENT_ENVIRONMENT_PEDIATRIC',
  PatientEthnicity = 'PATIENT_ETHNICITY',
  PatientExerciseRoutine = 'PATIENT_EXERCISE_ROUTINE',
  PatientFirstName = 'PATIENT_FIRST_NAME',
  PatientAssignedSexAtBirth = 'PATIENT_ASSIGNED_SEX_AT_BIRTH',
  PatientGenderIdentity = 'PATIENT_GENDER_IDENTITY',
  PatientGrossIncome = 'PATIENT_GROSS_INCOME',
  PatientGuarantors = 'PATIENT_GUARANTORS',
  PatientGuarantorVerification = 'PATIENT_GUARANTOR_VERIFICATION',
  PatientHomePhone = 'PATIENT_HOME_PHONE',
  PatientInsurance = 'PATIENT_INSURANCE',
  PatientInsuranceCardPhotoUpload = 'PATIENT_INSURANCE_CARD_PHOTO_UPLOAD',
  PatientInsuranceCardUpload = 'PATIENT_INSURANCE_CARD_UPLOAD',
  PatientLastName = 'PATIENT_LAST_NAME',
  PatientMaritalStatus = 'PATIENT_MARITAL_STATUS',
  PatientMedicalHistoryFamily = 'PATIENT_MEDICAL_HISTORY_FAMILY',
  PatientMedicalHistoryPersonal = 'PATIENT_MEDICAL_HISTORY_PERSONAL',
  PatientMedications = 'PATIENT_MEDICATIONS',
  PatientGeoPharmacy = 'PATIENT_GEO_PHARMACY',
  PatientNCPDPPharmacies = 'PATIENT_NCPDP_PHARMACIES',
  PatientPharmacies = 'PATIENT_PHARMACIES',
  PatientPhone = 'PATIENT_PHONE',
  PatientPhoneSameAsCell = 'PATIENT_PHONE_SAME_AS_CELL',
  PatientPhotoId = 'PATIENT_PHOTO_ID',
  PatientPhotoIdUpload = 'PATIENT_PHOTO_ID_UPLOAD',
  PatientPreferredFirstName = 'PATIENT_PREFERRED_FIRST_NAME',
  PatientPreferredLanguage = 'PATIENT_PREFERRED_LANGUAGE',
  PatientPreferredPhone = 'PATIENT_PREFERRED_PHONE',
  PatientPreferredPronoun = 'PATIENT_PREFERRED_PRONOUN',
  PatientProblems = 'PATIENT_PROBLEMS',
  PatientRace = 'PATIENT_RACE',
  PatientRelativeEmergencyContact = 'PATIENT_RELATIVE_EMERGENCY_CONTACT',
  PatientReligion = 'PATIENT_RELIGION',
  PatientRetirementDate = 'PATIENT_RETIREMENT_DATE',
  PatientSex = 'PATIENT_SEX',
  PatientSexualHealth = 'PATIENT_SEXUAL_HEALTH',
  PatientSexualIdentity = 'PATIENT_SEXUAL_IDENTITY',
  PatientSexualOrientation = 'PATIENT_SEXUAL_ORIENTATION',
  PatientSsn = 'PATIENT_SSN',
  PatientSsn4 = 'PATIENT_SSN4',
  PatientSubstanceUsage = 'PATIENT_SUBSTANCE_USAGE',
  PatientSubstanceUsageConcern = 'PATIENT_SUBSTANCE_USAGE_CONCERN',
  PatientSurgicalHistory = 'PATIENT_SURGICAL_HISTORY',
  PatientTobaccoUsage = 'PATIENT_TOBACCO_USAGE',
  PatientWorkPhone = 'PATIENT_WORK_PHONE',
  PatientWrittenLanguages = 'PATIENT_WRITTEN_LANGUAGE',
  Phone = 'PHONE',
  Registration = 'REGISTRATION',
  RelativeDate = 'RELATIVE_DATE',
  ScheduleAppointment = 'SCHEDULE_APPOINTMENT',
  ScheduleLink = 'SCHEDULE_LINK',
  ScheduleSeriesAppointment = 'SCHEDULE_SERIES_APPOINTMENT',
  Static = 'STATIC',
  String = 'STRING',
  TextInput = 'TEXT_INPUT',
  UsState = 'US_STATE',
  VirtualVisitInstructions = 'VIRTUAL_VISIT_INSTRUCTIONS',
}

export enum ScheduleAppointmentDisplayBy {
  Practice = 'PRACTICE',
  Provider = 'PROVIDER',
}

export enum ScheduleAppointmentSortBy {
  Alphabetically = 'ALPHABETICALLY',
  Availability = 'AVAILABILITY',
  Distance = 'DISTANCE',
}

export enum FhirDynamicProviderKind {
  PatientPCP = 'PATIENT_PCP',
  PatientPcpPreferred = 'PATIENT_PCP_PREFERRED',
}

export enum PatientOrderDisplayColumnOptions {
  RawFollowUpVisitType = 'rawFollowUpVisitType',
  ReferredToDepartment = 'referredToDepartment',
}

export interface TimeWithUnit {
  time: number;
  unit: TimingUnit;
}

export enum PatientCareProviderType {
  PatientPCP = 'PATIENT_PCP',
  PatientCareTeam = 'PATIENT_CARE_TEAM',
}

export enum PatientEmployerPrimitiveDisplayMode {
  Employer = 'Employer',
  Occupation = 'Occupation',
}

export enum ContinueButtonMode {
  Default = 'DEFAULT',
  Hidden = 'HIDDEN',
  Logout = 'LOGOUT',
  Feedback = 'FEEDBACK',
}

export const continueButtonModeMapping: Record<ContinueButtonMode, string> = {
  [ContinueButtonMode.Default]: 'Default',
  [ContinueButtonMode.Hidden]: 'Hidden',
  [ContinueButtonMode.Logout]: 'Logout',
  [ContinueButtonMode.Feedback]: 'Feedback',
};

export interface OptionMetadata {
  option: string;
  sortIndex: number;
  score: number | null;
  scores?: Record<string, number> | null;
  ehrExportValue?: string | null;
  displayValue?: string | null;
}

export interface FindAllLabelsOptions {
  q?: string;
  limit?: number;
  offset?: number;
  organizationId?: string;
}

export interface BasePrevisitFormTemplateFieldConfig {
  name: string;
  label: string;
  primitive: Primitive;
  page: number;
  rank: number;
  required: boolean;
  defaultValue: string;
  sublabel: string | null;
  placeholder: string | null;
  options: string[] | null;
  optionsMetadata: Record<string, OptionMetadata> | null;
  min: number | null;
  max: number | null;
  interval: number | null;
  prefix: string | null;
  suffix: string | null;
  dataBinding: string | null;
  helpDialog: string | null;
  helpDialogLabel: string | null;
  fhirAppointmentKinds: AppointmentKind[] | null;
  fhirTotalResult: number | null;
  fhirDynamicProviders: FhirDynamicProviderKind[] | null;
  scheduleAppointmentDisplayBy: ScheduleAppointmentDisplayBy | null;
  scheduleAppointmentSortBy: ScheduleAppointmentSortBy | null;
  scheduleAppointmentStartTime: TimeWithUnit | null;
  scheduleAppointmentEndTime: TimeWithUnit | null;
  scheduleAppointmentCallSectionEnabled: boolean;
  scheduleAppointmentCommentEnabled: boolean;
  hidePatientCareProvidersIfPopulated: boolean;
  lastPage: boolean | null;
  registrationEnableContactVerification: boolean | null;
  registrationFhirEhrReconciliation: PracticePreferenceFhirReconciliationType | null;
  registrationAdditionalLoginInputs: PatientLoginInput[] | null;
  registrationEhrProviderInformationId: string | null;
  scheduleAppointmentDefaultReasonForVisit: string | null;
  continueButtonMode: ContinueButtonMode | null;
  hideContinueButton: boolean;
  formQuestionEhrId: string | null;
  characterLimit: number | null;
  customConfigs: CustomConfigs;
}

export interface PaymentPrimitiveConfigs {
  hideCopay?: boolean;
  hidePrePay?: boolean;
  requireCopay?: boolean;
  requirePrePay?: boolean;
  hideNoBalancesPage?: boolean;
  hidePaymentFeedback?: boolean;
  continueButtonText?: string;
  payLaterButtonText?: string;
}

export interface AutomaticRedirectConfig {
  timeoutInMilliseconds: number;
  bodyText: string;
  calloutText: string;
  pollingStatusUpdates: {
    // after `elapsedMilliseconds`, update the loading indicator to render associated text and subtext.
    elapsedMilliseconds: number;
    mainText: string;
    subText?: string;
  }[];
  errorText: string;
  errorCalloutText: string;
}

export interface AutomaticRedirectConfigLocalization {
  bodyText?: string;
  calloutText?: string;
  errorText?: string;
  errorCalloutText?: string;
  pollingStatusUpdates?: {
    mainText?: string;
    subText?: string;
  }[];
}

export interface ScheduleAppointmentPrimitiveConfigs {
  appointmentCommentModalLabel?: string;
  appointmentCommentModalPlaceholder?: string;
  appointmentCommentModalTitle?: string;
  scheduleNextAvailableAppointmentEnabled?: boolean;
  patientOrderDisplayColumn?: PatientOrderDisplayColumnOptions;
  useReasonForVisitFromPatientOrders?: boolean;
  useHardcodedMammoCommentModal?: boolean;
  automaticRedirectConfig?: AutomaticRedirectConfig;
}

export interface ScheduleAppointmentPrimitiveConfigsLocalization {
  automaticRedirectConfig?: AutomaticRedirectConfigLocalization;
}

export interface ScheduleSeriesAppointmentPrimitiveConfigs {
  scheduleAppointmentSeriesMinTime?: TimeWithUnit;
  scheduleAppointmentSeriesMaxTime?: TimeWithUnit;
}

export interface PatientCareProvidersPickerPrimitiveConfigs {
  patientCareProviderType?: PatientCareProviderType;
  enabledProvidersOnly?: boolean;
}

export interface PatientEmployerPrimitiveConfigs {
  displayMode?: PatientEmployerPrimitiveDisplayMode;
}

export interface PatientSSNConfigs {
  hidePatientSsnIfPopulated?: boolean;
}

export interface PatientMedicalConditionsPrimitiveConfigs {
  hideOtherOption?: boolean;
}

export interface RegistrationPrimitiveConfigs {
  buttonTextOverride?: string;
  patientNotFoundErrorContent?: string;
  emailMayBeBlank?: boolean; // defaults to false, undefined resolves to default falsey case
  reconciliationFailedWithAttemptsRemainingErrorContent?: string;
  reconciliationFailedNoAttemptsRemainingErrorContent?: string;
  registrationMaxLoginAttempts?: number | null;
}

export interface NumberPrimitiveConfigs {
  numberOfDecimalDigits?: number;
}

export interface PhotoIdPrimitiveConfigs {
  extractDataFromBarcode?: boolean;
}

interface PatientInsurancePrimitiveConfigs {
  fhirCallForPatientInsurances?: boolean;
  selfPayFlatFeeAmount?: number;
}

export interface PatientSurgicalHistoryPrimitiveConfigs {
  surgeryTimeNotRequired?: boolean;
}

export interface PatientAllergiesPrimitiveConfigs {
  readOnly?: boolean;
}

export interface SkipExportingConfigs {
  skipFormFieldExporting?: boolean;
}

export interface CustomConfigs
  extends
    NumberPrimitiveConfigs,
    PaymentPrimitiveConfigs,
    PatientEmployerPrimitiveConfigs,
    PatientInsurancePrimitiveConfigs,
    PatientSSNConfigs,
    PatientMedicalConditionsPrimitiveConfigs,
    PatientCareProvidersPickerPrimitiveConfigs,
    ScheduleSeriesAppointmentPrimitiveConfigs,
    ScheduleAppointmentPrimitiveConfigs,
    RegistrationPrimitiveConfigs,
    PhotoIdPrimitiveConfigs,
    PatientSurgicalHistoryPrimitiveConfigs,
    PatientAllergiesPrimitiveConfigs,
    SkipExportingConfigs {}

export interface CustomConfigsLocalization extends ScheduleAppointmentPrimitiveConfigsLocalization {}

export interface PrevisitFormTemplateFieldConfig
  extends BasePrevisitFormTemplateFieldConfig, BaseItem {
  previsitFormTemplateId: string;
}

export interface PrevisitFormTemplateFieldConfigWithConditions extends PrevisitFormTemplateFieldConfig {
  conditions: Condition[];
  localizedOptions?: string[];
  localizedCustomConfigs?: CustomConfigsLocalization | null;
  fhirPracticeIds?: string[] | null;
  fhirProviderIds?: string[] | null;
}

export interface UpsertValues extends BasePrevisitFormTemplateFieldConfig {
  conditions: UpsertCondition[];
  localizations: UpsertLocalization[];
  fhirPracticeIds?: string[] | null;
  fhirProviderIds?: string[] | null;
}

export interface BaseLocalization {
  previsitFormTemplateLanguageId: string;
  previsitFormTemplateFieldConfigId: string;
  label: string;
  sublabel: string | null;
  options: string[] | null;
  helpDialog: string | null;
  helpDialogLabel: string | null;
  customConfigs: CustomConfigsLocalization;
}

export interface UpsertLocalization extends NullablePartial<BaseLocalization> {}

export interface PrevisitFormTemplateFieldConfigLocalization extends BaseItem, BaseLocalization {}

export enum InsuranceCardUploadProperties {
  Payors = 'payors',
  PayWithInsurance = 'payWithInsurance',
  Modified = 'modified',
  SkippedInsurance = 'skippedInsurance',
}

export enum PayWithInsuranceConditionOptions {
  PayWithInsurance = 'PAY_WITH_INSURANCE',
  PayOutOfPocket = 'PAY_OUT_OF_POCKET',
}

export enum BooleanConditionOptions {
  True = 'TRUE',
  False = 'FALSE',
}

export enum PatientGuarantorVerificationProperties {
  SomeoneElse = 'someoneElse',
  NewSelfGuarantor = 'newSelfGuarantor',
  EditExistingGuarantor = 'editExistingGuarantor',
}

export const STATIC_PRIMITIVES = [
  Primitive.Static,
  Primitive.CustomContent,
  Primitive.VirtualVisitInstructions,
];

export const REVIEWABLE_OPTIONAL_PRIMITIVES: Primitive[] = [
  Primitive.Enum,
  Primitive.PatientAssignedSexAtBirth,
  Primitive.PatientCareProviders,
  Primitive.PatientGenderIdentity,
];

export const REVIEWABLE_PRIMITIVES: Primitive[] = [
  Primitive.Enum,
  Primitive.Number,
  Primitive.PatientAddressMailing,
  Primitive.PatientAddressPhysical,
  Primitive.PatientAllergies,
  Primitive.PatientCareProviders,
  Primitive.PatientCellPhone,
  Primitive.PatientDob,
  Primitive.PatientEmail,
  Primitive.PatientEmergencyContact,
  Primitive.PatientEmergencyContacts,
  Primitive.PatientEmployer,
  Primitive.PatientEmploymentStatus,
  Primitive.PatientEthnicity,
  Primitive.PatientFirstName,
  Primitive.PatientAssignedSexAtBirth,
  Primitive.PatientGenderIdentity,
  Primitive.PatientGuarantors,
  Primitive.PatientHomePhone,
  Primitive.PatientInsurance,
  Primitive.PatientInsuranceCardPhotoUpload,
  Primitive.PatientInsuranceCardUpload,
  Primitive.PatientLastName,
  Primitive.PatientMaritalStatus,
  Primitive.PatientMedicalHistoryFamily,
  Primitive.PatientMedicalHistoryPersonal,
  Primitive.PatientGeoPharmacy,
  Primitive.PatientNCPDPPharmacies,
  Primitive.PatientPharmacies,
  Primitive.PatientPhotoIdUpload,
  Primitive.PatientPreferredFirstName,
  Primitive.PatientPreferredPhone,
  Primitive.PatientPreferredPronoun,
  Primitive.PatientPreferredLanguage,
  Primitive.PatientRace,
  Primitive.PatientReligion,
  Primitive.PatientRelativeEmergencyContact,
  Primitive.PatientRetirementDate,
  Primitive.PatientSex,
  Primitive.PatientSurgicalHistory,
  Primitive.PatientWorkPhone,
  Primitive.PatientWrittenLanguages,
  Primitive.Phone,
  Primitive.TextInput,
];

export const FULL_PAGE_PRIMITIVES: Primitive[] = [
  Primitive.BodyDiagram,
  Primitive.DocumentSigner,
  Primitive.FreehandBodyDiagram,
  Primitive.ImageUploader,
  Primitive.MultiScheduleAppointment,
  Primitive.PatientAllergies,
  Primitive.PatientBilling,
  Primitive.PatientBillingDetails,
  Primitive.PatientInsurance,
  Primitive.PatientInsuranceCardUpload,
  Primitive.PatientGeoPharmacy,
  Primitive.PatientMedications,
  Primitive.PatientPhotoIdUpload,
  Primitive.Registration,
  Primitive.ScheduleAppointment,
  Primitive.PatientSurgicalHistory,
  Primitive.PatientInsuranceCardPhotoUpload,
  Primitive.VirtualVisitInstructions,
];

export const CHARACTER_LIMIT_PRIMITIVES: Primitive[] = [Primitive.String, Primitive.TextInput];

// Universal primitives are primitives that should be synced on the front end
// for grouped encounters
export const UNIVERSAL_PRIMITIVES: Primitive[] = [
  Primitive.PatientAddressMailing,
  Primitive.PatientAddressMailingSameAsPhysical,
  Primitive.PatientAddressPhysical,
  Primitive.PatientAlcoholUsage,
  Primitive.PatientAlcoholUsageConcern,
  Primitive.PatientAllergies,
  Primitive.PatientCellPhone,
  Primitive.PatientDob,
  Primitive.PatientEmail,
  Primitive.PatientEmergencyContact,
  Primitive.PatientEmergencyContacts,
  Primitive.PatientEmployer,
  Primitive.PatientEmploymentStatus,
  Primitive.PatientEnvironment,
  Primitive.PatientEnvironmentPediatric,
  Primitive.PatientEthnicity,
  Primitive.PatientExerciseRoutine,
  Primitive.PatientFirstName,
  Primitive.PatientAssignedSexAtBirth,
  Primitive.PatientGenderIdentity,
  Primitive.PatientGrossIncome,
  Primitive.PatientHomePhone,
  Primitive.PatientLastName,
  Primitive.PatientMaritalStatus,
  Primitive.PatientMedicalHistoryFamily,
  Primitive.PatientMedicalHistoryPersonal,
  Primitive.PatientMedications,
  Primitive.PatientGeoPharmacy,
  Primitive.PatientPharmacies,
  Primitive.PatientNCPDPPharmacies,
  Primitive.PatientPhone,
  Primitive.PatientPhoneSameAsCell,
  Primitive.PatientPhotoId,
  Primitive.PatientPhotoIdUpload,
  Primitive.PatientPreferredFirstName,
  Primitive.PatientPreferredLanguage,
  Primitive.PatientPreferredPhone,
  Primitive.PatientPreferredPronoun,
  Primitive.PatientProblems,
  Primitive.PatientRace,
  Primitive.PatientRelativeEmergencyContact,
  Primitive.PatientReligion,
  Primitive.PatientRetirementDate,
  Primitive.PatientSex,
  Primitive.PatientSexualHealth,
  Primitive.PatientSexualIdentity,
  Primitive.PatientSexualOrientation,
  Primitive.PatientSsn,
  Primitive.PatientSsn4,
  Primitive.PatientSubstanceUsage,
  Primitive.PatientSubstanceUsageConcern,
  Primitive.PatientSurgicalHistory,
  Primitive.PatientTobaccoUsage,
  Primitive.PatientWorkPhone,
  Primitive.PatientWrittenLanguages,
];

export const BETA_PRIMITIVES: Primitive[] = [Primitive.PatientBilling];
